import * as React from 'react';
import { connect } from 'react-redux';

import { Form, FormField } from 'components/form';
import Input from 'components/inputs/input/Input';
import MixedSelectDropdown from 'components/inputs/mixed-select-dropdown/MixedSelectDropdown';
import InputRadio from 'components/inputs/input-radio/InputRadio';
import Popover from 'components/popover/Popover';
import Tooltip from 'components/Tooltip';
import { withModal } from 'components/modal/ModalHOC';

import AddressSettingsForm from '../AddressSettingsForm';
import TaxRegistrationExplanationModal from '../influencer-explanation-modals/tax-registration-explanation-modal/TaxRegistrationExplanationModal';
import BusinessDetailsExplanationModal from '../influencer-explanation-modals/business-details-explanation-modal/BusinessDetailsExplanationModal';
import InfluencerSettingsDocumentUpload from '../influencer-settings-document-upload/InfluencerSettingsDocumentUpload';

import {
  LONG_STRING_MAX_LENGTH,
  isRequired,
  isValidVATNumber,
  maxLength
} from 'utils/validators';
import { insertIf } from 'utils/list';
import { getPaymentDetailsCountryType } from 'utils/organization';
import { __t, applyTranslations } from 'utils/language';

import '../address-settings-form.scss';

import type { ChangeEvent } from 'react';
import type {
  LocationSuggestionCityOrCountry,
  MixedSelectDropdownItem,
  OrganizationSettingsForm,
  ProofDocumentFile,
  TextEntryKeyboardEvent,
  UploadFileResponse
} from 'types';

applyTranslations(require('../organizations-settings.i18n.json'));
applyTranslations(require('./influencer-business-details-settings-form.i18n.json'));

export type InputLocationProps = {
  city?: string,
  state?: string,
  country?: string
};

export type handlePhoneNumberEditFuncType = (
  value: string,
  field: string,
  countries?: Array<MixedSelectDropdownItem>
) => void;

type StateProps = {
  isDocumentProofSectionEnabled: boolean
}

type OwnProps = {
  values: {
    businessDetails: OrganizationSettingsForm['businessDetails'],
    billingAddress: OrganizationSettingsForm['billingAddress'],
    businessDetailsProof: UploadFileResponse | null
  },
  canEdit: boolean,
  isBrand?: boolean,
  hidePhone?: boolean,
  direction?: 'row' | 'column',
  labelType?: 'highlighted' | 'normal',
  countries?: Array<MixedSelectDropdownItem> | null,
  optional?: boolean,
  children?: React.ReactNode,
  isFromSettings?: boolean,
  onLocationInput: (event: TextEntryKeyboardEvent, location: LocationSuggestionCityOrCountry, type?: string) => void,
  onTextInput: (event: ChangeEvent<HTMLInputElement>, type: string, uppercase?: boolean) => void,
  onChange: (value: string | UploadFileResponse | null, name: string, type?: string) => void,
  onPreUpload: (
    value: ProofDocumentFile,
    name: string,
    type: string
  ) => void,
  onSubmit?: () => void,
  handlePhoneNumberEdit?: handlePhoneNumberEditFuncType | null
};

type Props = StateProps & OwnProps;

const ORG_MSG_KEY = 'organizations_settings';
const MSG_KEY = 'influencer_business_details_settings_form';
const TaxRegistrationExplanationWithModal = withModal(Tooltip, {
  modalContent: TaxRegistrationExplanationModal
});
const BusinessDetailsExplanationWithModal = withModal(Tooltip, {
  modalContent: BusinessDetailsExplanationModal
});

export const ENTREPRENEUR_OPTIONS = [
  {
    value: 'true',
    translation: __t(`${MSG_KEY}.labels.yes`)
  },
  {
    value: 'false',
    translation: __t(`${MSG_KEY}.labels.no`)
  }
];

const TAX_CATEGORY_OPTION = [
  {
    id: 'TAX_REGISTRATION_NUMBER',
    identifier: 'TAX_REGISTRATION_NUMBER',
    value: 'TAX_REGISTRATION_NUMBER',
    label: __t(`${MSG_KEY}.labels.dropdown_options.tax_registration_number`),
    type: 'category',
    isDisabled: false,
    isFocused: false,
    isSelected: false
  },
  {
    id: 'VAT_ID',
    identifier: 'VAT_ID',
    value: 'VAT_ID',
    label: __t(`${MSG_KEY}.labels.dropdown_options.vat_id`),
    type: 'category',
    isDisabled: false,
    isFocused: false,
    isSelected: false
  }
];

class InfluencerBusinessDetailsSettingsForm extends React.Component<Props> {
  handleRadioButtonSelect = (event: ChangeEvent<HTMLInputElement>, type: string) => {
    const { onTextInput } = this.props;
    onTextInput(event, type);
  };

  handleRegisteredBusinessTextInput = (event: ChangeEvent<HTMLInputElement>, type: string = 'billingAddress', uppercase?: boolean) => {
    const { onTextInput } = this.props;
    onTextInput(event, type, uppercase);
  };

  handleRegisteredBusinessLocationInput = (event: TextEntryKeyboardEvent, location: LocationSuggestionCityOrCountry) => {
    const { onLocationInput } = this.props;
    onLocationInput(event, location, 'billingAddress');
  };

  handleFocus = (event) => event.target.select();

  isEntrepreneur = () => {
    const { businessDetails } = this.props.values;
    return businessDetails.entrepreneur === true || businessDetails.entrepreneur === 'true';
  };

  renderCompanyNamePopover = () => (
    <div className="address-settings-form__popover-label">
      <Popover
        classNameWrapper="address-settings-form__popover-wrapper"
        classNamePopover="address-settings-form__popover"
        showQuestionMark
      >
        <div className="address-settings-form__popover-title">
          {__t(`${ORG_MSG_KEY}.labels.appears_on_invoice.title`)}
        </div>
        <div>
          {__t(`${ORG_MSG_KEY}.labels.appears_on_invoice.message`)}
          <a
            href={`mailto:${__t(`${ORG_MSG_KEY}.labels.appears_on_invoice.email`)}}`}
            className="address-settings-form__popover-link"
          >
            {__t(`${ORG_MSG_KEY}.labels.appears_on_invoice.email`)}
          </a>.
        </div>
      </Popover>
    </div>
  );

  renderCompanyDetailsForm = () => {
    const {
      values,
      direction,
      canEdit,
      labelType,
      optional,
      isFromSettings,
      isDocumentProofSectionEnabled,
      onChange,
      onPreUpload
    } = this.props;
    const { billingAddress, businessDetails, businessDetailsProof } = values;

    const width = direction === 'column' ? 'responsive' : 'fixed';

    const document = businessDetailsProof;
    const taxCategoryLabel = businessDetails.taxIdCategory ? __t(`${MSG_KEY}.labels.dropdown_options.${businessDetails.taxIdCategory.toLocaleLowerCase()}`) : '—';

    const countryType = getPaymentDetailsCountryType(
      billingAddress.country.toLowerCase().trim()
    );

    const isRequiredMasked = (value) => {
      if (optional) {
        return {
          valid: true,
          message: ''
        };
      }

      return isRequired(value);
    };

    return (
      <React.Fragment>
        <div className="influencer-business-details-settings__title">
          {__t(`${MSG_KEY}.labels.compnay_details_title`)}
          <div className="influencer-business-details-settings__tooltip-container">
            <TaxRegistrationExplanationWithModal
              message={__t(`${MSG_KEY}.please_click`)}
              width="normal"
              promptdisabled
              showQuestionMark
            />
          </div>
        </div>
        <FormField
          direction={direction}
          label={__t(`${MSG_KEY}.labels.company_name`)}
          labelType={labelType}
          fontSize="small"
          labelSize="custom"
          showRequiredMark
          showValidationIcon={!optional}
          validators={[
            isRequiredMasked(businessDetails.companyName),
            maxLength(businessDetails.companyName, LONG_STRING_MAX_LENGTH)
          ]}
        >
          <Input
            type="text"
            name="companyName"
            width={width}
            value={businessDetails.companyName}
            disabled={!canEdit}
            onChange={(event) => this.handleRegisteredBusinessTextInput(event, 'businessDetails')}
            onFocus={this.handleFocus}
          />
        </FormField>
        <FormField
          direction={direction}
          className="influencer-business-details-settings__tax-category-field"
          label={__t(`${MSG_KEY}.labels.tax_registration_number`)}
          labelType={labelType}
          labelSize="custom"
          fontSize="small"
          formFieldModal={BusinessDetailsExplanationWithModal}
          showRequiredMark
          showValidationIcon={!optional}
          validators={[
            isRequiredMasked(businessDetails.taxId),
            isRequiredMasked(businessDetails.taxIdCategory),
            ...insertIf(countryType !== 'non_eu' && businessDetails.taxIdCategory === 'VAT_ID', isValidVATNumber(businessDetails.taxId))
          ]}
        >
          {
            canEdit ? (
              <MixedSelectDropdown
                name="business-details-tax-registration-type"
                items={TAX_CATEGORY_OPTION.map(item => ({ ...item, isSelected: item.identifier === businessDetails.taxIdCategory }))}
                onItemsToggle={(items, data, selectedItem) => {
                  if (selectedItem?.identifier) {
                    onChange(selectedItem?.identifier, 'taxIdCategory', 'businessDetails');
                  }
                }}
                className="influencer-business-details-settings__tax-category-select"
              />
            ) : taxCategoryLabel
          }
          {
            businessDetails.taxIdCategory && (
              <Input
                type="text"
                name="taxId"
                width={width}
                value={businessDetails.taxId}
                placeholder={__t(`${MSG_KEY}.labels.placeholder.${businessDetails.taxIdCategory.toLocaleLowerCase()}`)}
                disabled={!canEdit}
                onChange={(event) => this.handleRegisteredBusinessTextInput(event, 'businessDetails', true)}
                onFocus={this.handleFocus}
              />
            )
          }
        </FormField>
        {
          isDocumentProofSectionEnabled && (
            <FormField
              direction={canEdit ? 'column' : direction}
              className={`influencer-business-details-settings__tax-document-container-${canEdit ? 'edit' : 'view'}`}
              label={canEdit ? '' : __t(`${MSG_KEY}.labels.tax_registration_document.title`)}
              labelType={labelType}
              labelSize="custom"
              fontSize="small"
            >
              <InfluencerSettingsDocumentUpload
                title={__t(`${MSG_KEY}.labels.tax_registration_document.title`)}
                info={__t(`${MSG_KEY}.labels.tax_registration_document.info`)}
                uploadButtonLabel={__t(`${MSG_KEY}.labels.tax_registration_document.submit_btn`)}
                document={document}
                canEdit={canEdit}
                isFromSettings={isFromSettings}
                onDelete={() => onChange(null, 'businessDetailsProof', 'proofDocuments')}
                onPreUpload={(value: ProofDocumentFile) => (
                  onPreUpload(value, 'businessDetailsProof', 'proofDocuments')
                )}
              />
            </FormField>
          )
        }
        {
          countryType === 'germany' && (
            <FormField
              direction={direction}
              label={__t(`${ORG_MSG_KEY}.labels.entrepreneur`)}
              labelType={labelType}
              labelSize="custom"
              fontSize="small"
              showRequiredMark
              showValidationIcon={!optional}
              helpNote={canEdit ? __t(`${ORG_MSG_KEY}.labels.entrepreneur_info`) : undefined}
              validators={[
                isRequiredMasked(String(businessDetails.entrepreneur))
              ]}
            >
              {
                  ENTREPRENEUR_OPTIONS.map(entry => {
                    const isChecked = String(businessDetails.entrepreneur) === entry.value;

                    return canEdit ?
                      <InputRadio
                        key={`entrepreneur-${entry.value}`}
                        name="entrepreneur"
                        value={String(entry.value)}
                        id={`entrepreneur-${entry.value}`}
                        checked={isChecked}
                        label={entry.translation}
                        onChange={(event) => this.handleRadioButtonSelect(event, 'businessDetails')}
                      /> :
                      <span key={`entrepreneur-${entry.value}`}>
                        {isChecked ? entry.translation : ''}
                      </span>;
                  })
                }
            </FormField>
          )
        }
      </React.Fragment>
    );
  };

  render() {
    const {
      values,
      direction,
      onSubmit,
      canEdit,
      isBrand,
      hidePhone,
      labelType,
      children,
      countries,
      optional,
      handlePhoneNumberEdit
    } = this.props;
    const { billingAddress } = values;

    const renderFields = () => (
      <React.Fragment>
        <div className="address-settings-form__address">
          {this.renderCompanyDetailsForm()}
          <div className="influencer-business-details-settings__title influencer-business-details-settings__address_form">
            {__t(`${MSG_KEY}.labels.business_address_details_title`)}
          </div>
          <AddressSettingsForm
            direction={direction}
            labelType={labelType}
            labelSize="custom"
            hidePhone={hidePhone}
            onLocationInput={this.handleRegisteredBusinessLocationInput}
            onTextInput={this.handleRegisteredBusinessTextInput}
            values={billingAddress}
            canEdit={canEdit}
            isBrand={isBrand}
            countries={countries}
            optional={optional}
            handlePhoneNumberEdit={handlePhoneNumberEdit}
          />
        </div>
        {children}
      </React.Fragment>
    );

    if (!onSubmit) {
      return renderFields();
    }

    return (
      <div>
        <Form
          key={`registered-business-form-${canEdit}`}
          onSubmit={onSubmit}
        >
          {renderFields()}
        </Form>
      </div>
    );
  }
}

const mapStateToProps = ({ runtimeConfig }): StateProps => ({
  isDocumentProofSectionEnabled: runtimeConfig?.config?.settingsDocumentProofEnabled
});

export default connect(mapStateToProps)(InfluencerBusinessDetailsSettingsForm);
